/**
 * useCardAvailability - Computes per-edition inventory, deck usage, and net availability
 *
 * Shows which editions of a card the user owns, which are assigned to decks,
 * and the net availability (owned - committed) per edition.
 *
 * Only active decks hold physical copies, so only their usage is subtracted.
 * Brewing decks ("planned") and maybeboards are reported separately.
 */
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { collectionOwnedByScryfallIdAtom } from '@/features/collection/state/collectionAtom';
import { allDeckCardsAtom } from '@/features/decks/state/allDeckCards';
import { decksStateAtom } from '@/features/decks/state/decksAtom';
import { availabilityCardsByScryfallIdAtom, classifyDeckUsage, type DeckUsageKind } from '@/state/cardAvailability';
import type { DeckStatus } from '@/types/deck';

export interface DeckUsage {
  deckId: string;
  deckName: string;
  deckStatus: DeckStatus;
  qty: number;
  kind: DeckUsageKind;
}

export interface EditionAvailability {
  scryfallId: string;
  setCode: string;
  collectorNumber: string;
  inventoryQty: number;
  deckUsages: DeckUsage[];
  /** Copies held by active decks — the only usage that consumes availability */
  committedQty: number;
  /** Copies earmarked by brewing decks, which reserve nothing */
  plannedQty: number;
  maybeboardQty: number;
  available: number;
}

export interface CardAvailabilityResult {
  editions: EditionAvailability[];
  totals: { inventory: number; committed: number; planned: number; maybeboard: number; available: number };
  isLoading: boolean;
}

const USAGE_SORT_ORDER: Record<DeckUsageKind, number> = { committed: 0, planned: 1, maybeboard: 2 };

export default function useCardAvailability(oracleId: string | undefined, cardName: string | undefined): CardAvailabilityResult {
  const cardsById = useAtomValue(availabilityCardsByScryfallIdAtom);
  const ownedByScryfall = useAtomValue(collectionOwnedByScryfallIdAtom);
  const allDeckCards = useAtomValue(allDeckCardsAtom);
  const decksState = useAtomValue(decksStateAtom);

  return useMemo(() => {
    const isLoading = allDeckCards.isLoading || decksState.isLoading;

    if (!oracleId && !cardName) {
      return { editions: [], totals: { inventory: 0, committed: 0, planned: 0, maybeboard: 0, available: 0 }, isLoading };
    }

    const nameLower = cardName?.toLowerCase();
    const printingScryfallIds = Array.from(cardsById.values())
      .filter((card) => (oracleId ? card.oracle_id === oracleId : card.name_lower === nameLower))
      .map((card) => card.id);

    const deckById = new Map((decksState.data ?? []).map((deck) => [deck.id, deck]));

    // Per-printing usage, keyed by deck + usage kind so a deck can appear once per tier
    const usageByScryfall = new Map<string, Map<string, DeckUsage>>();
    if (allDeckCards.data) {
      const printingSet = new Set(printingScryfallIds);
      for (const dc of allDeckCards.data) {
        if (!printingSet.has(dc.scryfall_id)) continue;
        const deck = deckById.get(dc.deck_id);
        const kind = classifyDeckUsage(deck?.status, dc.zone);
        if (!deck || !kind) continue; // archived, deleted, or unknown deck

        let byKey = usageByScryfall.get(dc.scryfall_id);
        if (!byKey) {
          byKey = new Map();
          usageByScryfall.set(dc.scryfall_id, byKey);
        }
        const key = `${dc.deck_id}:${kind}`;
        const existing = byKey.get(key);
        if (existing) {
          existing.qty += dc.quantity;
        } else {
          byKey.set(key, { deckId: dc.deck_id, deckName: deck.name, deckStatus: deck.status, qty: dc.quantity, kind });
        }
      }
    }

    // Build edition rows (only editions with activity)
    const editions: EditionAvailability[] = [];
    for (const scryfallId of printingScryfallIds) {
      const inventoryQty = ownedByScryfall.get(scryfallId) ?? 0;
      const deckUsages = Array.from(usageByScryfall.get(scryfallId)?.values() ?? []);

      if (inventoryQty === 0 && deckUsages.length === 0) continue;

      let committedQty = 0;
      let plannedQty = 0;
      let maybeboardQty = 0;
      for (const usage of deckUsages) {
        if (usage.kind === 'committed') committedQty += usage.qty;
        else if (usage.kind === 'planned') plannedQty += usage.qty;
        else maybeboardQty += usage.qty;
      }

      // Sort: committed, then planned, then maybeboard — each by qty descending
      deckUsages.sort((a, b) => USAGE_SORT_ORDER[a.kind] - USAGE_SORT_ORDER[b.kind] || b.qty - a.qty);

      const card = cardsById.get(scryfallId);
      editions.push({
        scryfallId,
        setCode: card?.set ?? '',
        collectorNumber: card?.collector_number ?? '',
        inventoryQty,
        deckUsages,
        committedQty,
        plannedQty,
        maybeboardQty,
        available: inventoryQty - committedQty,
      });
    }

    // Sort: editions with negative availability first, then by set code
    editions.sort((a, b) => {
      if (a.available < 0 && b.available >= 0) return -1;
      if (b.available < 0 && a.available >= 0) return 1;
      return a.setCode.localeCompare(b.setCode) || a.collectorNumber.localeCompare(b.collectorNumber);
    });

    const totals = editions.reduce(
      (acc, e) => ({
        inventory: acc.inventory + e.inventoryQty,
        committed: acc.committed + e.committedQty,
        planned: acc.planned + e.plannedQty,
        maybeboard: acc.maybeboard + e.maybeboardQty,
        available: acc.available + e.available,
      }),
      { inventory: 0, committed: 0, planned: 0, maybeboard: 0, available: 0 },
    );

    return { editions, totals, isLoading };
  }, [oracleId, cardName, cardsById, ownedByScryfall, allDeckCards, decksState]);
}
